import { useState, useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLazyQuery, useLazyCommand, useEvent } from '@soap/modules';
import { ChevronDown, Eraser, X } from 'lucide-react';
import Alex from '/z30-images/robot-s.png';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { AlexMarkdownMessage } from './AlanMarkdownMessage';
import { HelpChatEmailSupportControls } from './HelpChatEmailSupportControls';
import { useHelpChat } from './HelpChatProvider';
import { collectHelpPageContext } from '../utils/collectHelpPageContext';
import {
  disableSessionDebugMode,
  enableSessionDebugMode,
} from '../../constants/sessionDebugMode';
import { useSyncLywSessionDebugMode } from '../hooks/useSyncLywSessionDebugMode';

type ChatMessageDto = {
  e305_UserMessage?: string;
  e305_AgentResponse?: string;
  e305_CreatedAt?: string;
};

const TIMESTAMP_MIN_AGE_MS = 10 * 60 * 1000;

type SessionDebugChatCommand = 'enable' | 'disable';

/** Alex chat: hidden commands (trimmed, case-insensitive, internal spaces collapsed) — no backend call. */
function getSessionDebugChatCommand(raw: string): SessionDebugChatCommand | null {
  const n = raw.trim().replace(/\s+/g, ' ').toLowerCase();
  if (n === 'enable debug mode') return 'enable';
  if (n === 'disable debug mode') return 'disable';
  return null;
}

function formatMessageTime(iso?: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function shouldShowTimestamp(iso?: string): boolean {
  if (!iso) return false;
  try {
    const d = new Date(iso);
    return Date.now() - d.getTime() > TIMESTAMP_MIN_AGE_MS;
  } catch {
    return false;
  }
}

export function HelpChatPanel() {
  const { close } = useHelpChat();
  const isDesktop = useMediaQuery({ query: '(min-width: 640px)' });
  const [messages, setMessages] = useState<ChatMessageDto[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const pendingMessageRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [getChatHistory] = useLazyQuery('C304v1_GetChatHistory', null);
  const [sendChatMessage] = useLazyCommand('C303v1_SendChatMessage');
  const [clearHelpChatSession] = useLazyCommand('C306v1_ClearHelpChatSession');
  const syncSessionDebugMode = useSyncLywSessionDebugMode();

  useEvent({
    eventName: 'E305v1_GotChatHistory',
    onEventReceived: (event: { e305_Messages?: ChatMessageDto[] }) => {
      setMessages(event.e305_Messages ?? []);
    },
  });

  useEvent({
    eventName: 'E303v1_AgentResponse',
    onEventReceived: (event: { e303_ResponseText?: string }) => {
      const userMsg = pendingMessageRef.current ?? '';
      pendingMessageRef.current = null;
      setMessages((prev) => [
        ...prev,
        {
          e305_UserMessage: userMsg,
          e305_AgentResponse: event.e303_ResponseText ?? 'Thanks, I got your message.',
          e305_CreatedAt: new Date().toISOString(),
        },
      ]);
      setSending(false);
    },
  });

  useEffect(() => {
    getChatHistory?.();
  }, [getChatHistory]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages]);

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text || sending) return;

    const debugCmd = getSessionDebugChatCommand(text);
    if (debugCmd) {
      if (debugCmd === 'enable') {
        enableSessionDebugMode();
        void syncSessionDebugMode(true);
        setMessages((prev) => [
          ...prev,
          {
            e305_UserMessage: text,
            e305_AgentResponse: 'Debug mode enabled for this session.',
            e305_CreatedAt: new Date().toISOString(),
          },
        ]);
      } else {
        disableSessionDebugMode();
        void syncSessionDebugMode(false);
        setMessages((prev) => [
          ...prev,
          {
            e305_UserMessage: text,
            e305_AgentResponse: 'Debug mode disabled for this session.',
            e305_CreatedAt: new Date().toISOString(),
          },
        ]);
      }
      setInputValue('');
      return;
    }

    pendingMessageRef.current = text;
    setInputValue('');
    setSending(true);
    sendChatMessage({
      C303_Message: text,
      C303_PageContext: collectHelpPageContext(),
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      data-tour="help-chat"
      className="flex h-full min-h-0 flex-col bg-white"
    >
      <div className="flex flex-row items-center justify-between gap-2 border-b bg-gray-50 px-3 py-2 sm:px-4">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {!isDesktop && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 shrink-0 p-0 text-gray-600 hover:text-gray-900"
              onClick={close}
              aria-label="Close chat"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          )}
          <img src={Alex} alt="Alex" className="h-8 w-8 shrink-0 rounded-full" />
          <span className="truncate font-medium">Chat with Alex</span>
        </div>
        <div className="flex shrink-0 flex-nowrap items-center gap-1">
          <HelpChatEmailSupportControls
            messages={messages}
            chatBusy={sending}
            onSupportEmailQueued={(AlexConfirmationMarkdown) =>
              setMessages((prev) => [
                ...prev,
                {
                  e305_AgentResponse: AlexConfirmationMarkdown,
                  e305_CreatedAt: new Date().toISOString(),
                },
              ])
            }
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0 gap-1 text-gray-600 hover:text-gray-900"
            disabled={sending || messages.length === 0}
            onClick={() => clearHelpChatSession({})}
            aria-label="Clear chat history"
          >
            <Eraser className="h-4 w-4 shrink-0" />
            <span className="sm:hidden">Clear</span>
            <span className="hidden sm:inline">Clear chat</span>
          </Button>
          {isDesktop && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 shrink-0 p-0 text-gray-600 hover:text-gray-900"
              onClick={close}
              aria-label="Close chat"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          className="flex-1 space-y-4 overflow-y-auto p-4"
        >
          {messages.length === 0 && (
            <div className="space-y-2">
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-800">
                  Hi, how can I help?
                </div>
              </div>
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-800">
                  By the way, I can see what you&apos;re working on right now — so feel free to ask about this screen.
                </div>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className="space-y-2">
              {m.e305_UserMessage && (
                <div className="flex flex-col items-end">
                  <div className="max-w-[80%] rounded-lg bg-[#1a5172] px-3 py-2 text-sm text-white">
                    {m.e305_UserMessage}
                  </div>
                  {shouldShowTimestamp(m.e305_CreatedAt) && (
                    <span className="mt-0.5 text-[10px] text-gray-500">
                      {formatMessageTime(m.e305_CreatedAt)}
                    </span>
                  )}
                </div>
              )}
              {m.e305_AgentResponse && (
                <div className="flex flex-col items-start">
                  <div className="max-w-[80%] rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-800">
                    <AlexMarkdownMessage content={m.e305_AgentResponse} />
                  </div>
                  {shouldShowTimestamp(m.e305_CreatedAt) && (
                    <span className="mt-0.5 text-[10px] text-gray-500">
                      {formatMessageTime(m.e305_CreatedAt)}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {sending && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-white/70"
            aria-label="Alex is typing"
          >
            <div className="flex max-w-[80%] justify-start">
              <div className="rounded-lg bg-gray-100 px-4 py-3">
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="h-2 w-2 animate-bounce rounded-full bg-gray-500"
                      style={{
                        animationDelay: `${i * 0.15}s`,
                        animationDuration: '0.6s',
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="border-t p-3 pt-2 sm:p-4 sm:pt-3">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            disabled={sending}
            className="flex-1"
          />
          <Button
            onClick={handleSend}
            disabled={sending || !inputValue.trim()}
            className="bg-[#1a5172] hover:bg-[#164a65]"
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </div>
        <p className="mt-2 text-[10px] leading-snug text-gray-400">
          Disclaimer: Alex can make mistakes.
        </p>
      </div>
    </div>
  );
}
